"use client";

import { useCallback, useRef } from "react";
import type { Id } from "@convex/_generated/dataModel";
import { UPLOAD_CLAIM_WINDOW_MS } from "@convex/constants/uploadPolicies.lib";
import { uploadToStorage } from "@/lib/files/upload-to-storage";

const CLOCK_SKEW_MARGIN_MS = 5 * 60 * 1000;
export const REUSABLE_UPLOAD_MS = UPLOAD_CLAIM_WINDOW_MS - CLOCK_SKEW_MARGIN_MS;

type CachedUpload = { file: File; storageId: Id<"_storage">; uploadedAt: number };

export function useOnceUpload(generateUploadUrl: (args: Record<string, never>) => Promise<string>) {
  const uploaded = useRef<CachedUpload | null>(null);
  return useCallback(
    async (file: File): Promise<Id<"_storage">> => {
      const cached = uploaded.current;
      if (cached?.file === file && Date.now() - cached.uploadedAt < REUSABLE_UPLOAD_MS) {
        return cached.storageId;
      }
      const storageId = await uploadToStorage(await generateUploadUrl({}), file);
      uploaded.current = { file, storageId, uploadedAt: Date.now() };
      return storageId;
    },
    [generateUploadUrl],
  );
}
